import type { Verdict } from './types';

export interface HeatmapCell {
  /** 0-6, oldest first (row 6 = today). */
  day: number;
  /** 0-23, site-local. */
  hour: number;
  /** Saturated minutes in this cell. */
  count: number;
  /** Minutes that cleared the packet floor in this cell — the denominator. */
  measured: number;
  /** Minutes the sampler produced at all, before the packet floor.
   *  `sampled === 0` is the ONLY genuine "no data"; `sampled > 0 && measured
   *  === 0` is an idle hour, which is a fact about the network, not a gap in
   *  monitoring. */
  sampled: number;
  /** count/measured, 0 when nothing was measured. Presentation only: it drives
   *  opacity WITHIN a band. It must never be re-banded here. */
  share: number;
  /** Decided by the backend (crm/capacity.py::_cell_verdict). */
  verdict: Verdict;
  /** Download-direction slice of this cell — absent when the payload carries
   *  no per-direction grids (member portal, older backend). */
  down?: HeatmapDirection;
  /** Upload-direction slice, same rules as `down`. */
  up?: HeatmapDirection;
}

/** One direction of one cell. */
export interface HeatmapDirection {
  /** Minutes saturated in this direction. */
  count: number;
  /** count / the cell's `measured` (the SAME denominator as the combined
   *  share). Presentation only — drives opacity within the band. */
  share: number;
  /** Decided by the backend (crm/capacity.py, cell_verdicts_down/_up). */
  verdict: Verdict;
}

/** The four per-direction 7x24 arrays the backend serves, together: a
 *  half-supplied set is the caller's to refuse (see `CapacityHeatmap`), so one
 *  triangle is never painted from data and the other from a default. */
export interface HeatmapDirectionGrids {
  gridDown: number[][];
  gridUp: number[][];
  cellVerdictsDown: Verdict[][];
  cellVerdictsUp: Verdict[][];
}

/**
 * Pure so it is unit-testable in the repo's node-env vitest (no DOM).
 *
 * Zips the three per-cell arrays the backend serves. It deliberately does NOT
 * decide anything: `grid` alone could never express "healthy" (a 0 means either
 * measured-and-fine or never-sampled), and the band edges are per-customer
 * configurable, so both facts are the backend's to state.
 */
export const heatmapCells = (
  grid: number[][],
  measuredGrid: number[][],
  cellVerdicts: Verdict[][],
  sampledGrid?: number[][],
  directions?: HeatmapDirectionGrids,
): HeatmapCell[] => {
  const cells: HeatmapCell[] = [];
  grid.forEach((row, day) =>
    row.forEach((count, hour) => {
      const measured = measuredGrid[day]?.[hour] ?? 0;
      // Optional so a caller on an older payload still renders. Falling back to
      // `measured` (not 0) is deliberate: it reproduces the previous
      // behaviour — every unmeasured cell reads as unsampled — rather than
      // inventing an "idle" state from data that is not there.
      const sampled = sampledGrid?.[day]?.[hour] ?? measured;
      // Same denominator as the combined share: a direction reads as a share
      // of the cell's busy minutes, which is what the backend banded it on.
      const direction = (counts: number[][], verdicts: Verdict[][]): HeatmapDirection => {
        const n = counts[day]?.[hour] ?? 0;
        return {
          count: n,
          share: measured > 0 ? n / measured : 0,
          verdict: verdicts[day]?.[hour] ?? 'insufficient_data',
        };
      };
      cells.push({
        day,
        hour,
        count,
        measured,
        sampled,
        share: measured > 0 ? count / measured : 0,
        verdict: cellVerdicts[day]?.[hour] ?? 'insufficient_data',
        ...(directions && {
          down: direction(directions.gridDown, directions.cellVerdictsDown),
          up: direction(directions.gridUp, directions.cellVerdictsUp),
        }),
      });
    }),
  );
  return cells;
};
